/* eslint-disable jira/naming/disallow-filenames */
// eslint-disable-next-line jira/filename-case
import React, { useEffect, useCallback, useState } from 'react';
import StarStarredIcon from '@atlaskit/icon/core/star-starred';
import StarUnstarredIcon from '@atlaskit/icon/core/star-unstarred';
import ButtonItem from '@atlaskit/menu/button-item';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';
import type { ItemTypename } from '@jira/platform__favouriting-favourite-change-provider/src/model/types.tsx';
import { useChangeFavorite } from '@jira/platform__favouriting-favourite-change-provider/src/view/FavoriteChangeConsumer.tsx';
import { useIntl, defineMessage } from '@atlassian/jira-intl';
import { useStopPropagation } from '../../common/utils/use-stop-propagation/index.tsx';

const addToStarred = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.favorite-toggle-button.add-to-starred',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Add to starred',
	description: 'Used as the menu-title for the Add to Starred menu button item',
});

const removeFromStarred = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.favorite-toggle-button.remove-from-starred',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Remove from starred',
	description: 'Used as the menu-title for the Remove from Starred menu button item',
});

const addToStarredWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.favorite-toggle-button.add-to-starred-with-name',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Add "{name}" to starred',
	description:
		'Used as the alt text for the "add to starred" menu button item. The {name} attribute refers to the name of the entity that should be added to starred. For example, this could be a filter name like "blocked issues" and the full message would be "Add blocked issues to starred".',
});

const removeFromStarredWithName = defineMessage({
	id: 'navigation-apps-sidebar-nav4-common.favorite-toggle-button.remove-from-starred-with-name',
	// eslint-disable-next-line @atlassian/tangerine/i18n/messages-are-spelled-correctly
	defaultMessage: 'Remove "{name}" from starred',
	description:
		'Used as the alt text for the "remove from starred" menu button item. The {name} attribute refers to the name of the entity that should be remove from starred. For example, this could be a filter name like "blocked issues" and the full message would be "Remove blocked issues from starred".',
});

export type FavoriteToggleButtonProps = {
	entityId: string;
	entityTypeName: ItemTypename;
	defaultIsStarred?: boolean;
	onClick?: () => void;
	name?: string;
};

const UnstarredIcon = <StarUnstarredIcon label="" color="currentColor" spacing="spacious" />;

const StarredIcon = (
	<StarStarredIcon label="" color={token('color.icon.accent.yellow')} spacing="spacious" />
);

export const FavoriteToggleButton = ({
	entityId,
	entityTypeName,
	defaultIsStarred = false,
	name,
	onClick,
}: FavoriteToggleButtonProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isStarred, setIsStarred] = useState(defaultIsStarred);

	const { changeFavoriteMutation } = useChangeFavorite();

	const handleOnClick = useCallback(() => {
		setIsStarred((prevIsStarred) => !prevIsStarred);
		onClick?.();
	}, [onClick]);

	useEffect(() => {
		if (isStarred !== defaultIsStarred) {
			changeFavoriteMutation({
				id: entityId,
				typename: entityTypeName,
				value: isStarred,
			});
		}
	}, [entityId, entityTypeName, isStarred, defaultIsStarred, changeFavoriteMutation]);

	const handleOnClickWithStopPropagation = useStopPropagation(handleOnClick);

	return isStarred ? (
		<ButtonItem
			iconBefore={StarredIcon}
			onClick={handleOnClickWithStopPropagation}
			aria-label={
				name ? formatMessage(removeFromStarredWithName, { name }) : formatMessage(removeFromStarred)
			}
		>
			<Text color="color.text">{formatMessage(removeFromStarred)}</Text>
		</ButtonItem>
	) : (
		<ButtonItem
			iconBefore={UnstarredIcon}
			onClick={handleOnClickWithStopPropagation}
			aria-label={
				name ? formatMessage(addToStarredWithName, { name }) : formatMessage(addToStarred)
			}
		>
			<Text color="color.text">{formatMessage(addToStarred)}</Text>
		</ButtonItem>
	);
};
